import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Alert, Button } from '@patternfly/react-core';
import SplashPage from '../splash/SplashPage';
import { useTranslation } from 'react-i18next';

import './AuthenticatedPage.css';
import { ACCESS_RIGHTS_VALID, isInitialAccessRights } from '../../store/user/access-rights';
import { StoreState } from '../../store/store';
import { authSelectors, logOutRequested } from '../../store/auth/authSlice';
import { userSelectors } from '../../store/user/userSlice';
import { ProfileButton } from '../../components/ProfileButton';

/** Wrapper component to use when the app requires cloud authentication. Otherwise use NoAuthPage. */
const AuthenticatedPage = (props: React.PropsWithChildren) => {
    const { t } = useTranslation();

    const dispatch = useDispatch();

    // const auths = useSelector((state: StoreState) => state.appAuthStates);
    const loginError = useSelector((state: StoreState) => state.auth.loginError);
    const accessRights = useSelector(userSelectors.selectAccessRights);
    const accessRightsError = useSelector(userSelectors.selectAccessRightsError);

    const isWaitingForLogin = !useSelector(authSelectors.selectIsLoggedIntoAtLeastOneAppAuth);
    const wasLoginSuccessful = loginError === null && accessRightsError === null;
    const hasAccessRights = wasLoginSuccessful && accessRights && accessRights.configui.includes(ACCESS_RIGHTS_VALID);
    const isLoadingAccessRights = wasLoginSuccessful && isInitialAccessRights(accessRights);
    const loginErrorMessage = accessRightsError !== null ? accessRightsError : !wasLoginSuccessful && loginError && loginError.message;

    const handleLogOut = () => dispatch(logOutRequested());

    return (
        <>
            {!wasLoginSuccessful && loginErrorMessage && (<Alert variant="danger" title={t('error.loginError.title', { loginErrorMessage: loginErrorMessage })}>
                <p>{t('error.loginError.paragraph1')}</p>
                <p>{loginErrorMessage}</p>
                <p>{t('error.loginError.paragraph2')}</p>
            </Alert>)}
            {(isWaitingForLogin || isLoadingAccessRights) && (<SplashPage loadingText={t('splash.waitingForLogin')} />)}
            

            {!isWaitingForLogin && !hasAccessRights && !isInitialAccessRights(accessRights) && !isLoadingAccessRights && (
                <>
                    <div className="profile-button-overlay">
                        <ProfileButton />
                    </div>
                    <div className="access-rights-error-container">
                        <SplashPage loadingText={t('error.accessRightsError')} />
                        <div className="logout-button-container">
                            <Button variant="primary" onClick={handleLogOut}>{t('common.logOut')}</Button>
                        </div>
                    </div>
                </>
            )}
            {!isWaitingForLogin && !isLoadingAccessRights && !isInitialAccessRights(accessRights) && hasAccessRights && props.children}
        </>
    );
}

export default AuthenticatedPage;
